import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { images } from '../data/translations';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function VisualShowcase() {
  const { t } = useLanguage();
  const captions = t.gallery.captions;
  const galleryImages = images.gallery;
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef(null);

  const startAutoScroll = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % galleryImages.length);
    }, 4000);
  };

  useEffect(() => {
    startAutoScroll();
    return () => clearInterval(intervalRef.current);
  }, [galleryImages.length]);

  const goTo = (i) => {
    setCurrent(i);
    startAutoScroll();
  };

  const prev = () => goTo((current - 1 + galleryImages.length) % galleryImages.length);
  const next = () => goTo((current + 1) % galleryImages.length);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-24 bg-[#0F1629]"
    >
      <div className="max-w-6xl mx-auto px-6 mb-10">
        <span className="text-[#E71C25] text-xs font-bold tracking-[0.3em] uppercase">
          {t.nav.gallery}
        </span>
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Main image */}
        <div className="relative aspect-[16/9] overflow-hidden rounded-lg">
          {galleryImages.map((img, i) => (
            <div
              key={i}
              data-testid={`gallery-slide-${i}`}
              className={`absolute inset-0 transition-all duration-700 ${
                i === current ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
              }`}
            >
              <img
                src={img}
                alt={captions[i]}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>
          ))}

          {/* Caption */}
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <p
              data-testid="gallery-caption"
              className="text-white text-sm md:text-base font-medium tracking-wide"
            >
              {captions[current]}
            </p>
          </div>

          {/* Arrow controls */}
          <button
            onClick={prev}
            data-testid="gallery-prev"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-white hover:bg-black/60 transition-all duration-300"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={next}
            data-testid="gallery-next"
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/70 hover:text-white hover:bg-black/60 transition-all duration-300"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2.5 mt-6">
          {galleryImages.map((_, i) => (
            <button
              key={i}
              data-testid={`gallery-dot-${i}`}
              onClick={() => goTo(i)}
              className={`h-1 rounded-full transition-all duration-400 ${
                i === current ? 'w-8 bg-[#E71C25]' : 'w-4 bg-white/15 hover:bg-white/25'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
